import React, { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useHistory } from 'react-router-dom';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import useFetch from 'use-http';

import { Button } from '@/components/ui/button';
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import SelectField from '@/components/shared/SelectField';
import FileUploadAsBase64 from '@/components/shared/FileUploadAsBase64/FileUploadAsBase64';
import { useUserState } from '@/components/context/UserContext';
import personalCodeValidation from '@/components/hooks/personalCodeValidation';
import { Heading } from '@/components/ui/shared/heading';
import { MaterialIcon } from '@/components/ui/shared/materialIcon';

const DutyDoctorRegistrationForm = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const userState = useUserState();
  const isTestEnv = process.env.REACT_APP_ISTESTENV === 'true';
  const checkboxRef = useRef();

  const [modalVisible, setModalVisible] = useState(false);
  const [modalType, setModalType] = useState('success');
  const [modalText, setModalText] = useState('');

  const [validatePersonalCode] = personalCodeValidation();
  const topicSelectRef = useRef();
  const [topicList, setTopicList] = useState([]);
  const [selectedTopicLabel, setSelectedTopicLabel] = useState(null);

  const { request: fileRequest } = useFetch(`/Files`);
  const { post: createRegistration, response: createRegistrationResponse } =
    useFetch(`/LocalRegistration/RegisterDoctorOnCall`);

  const formSchema = z.object({
    name: z.string().min(1, t('validation.requiredField')),
    surname: z.string().min(1, t('validation.requiredField')),
    personalCode: z
      .string()
      .min(1, t('validation.requiredField'))
      .refine((val) => validatePersonalCode(val), {
        message: t('validation.invalidFormat'),
      }),
    phone: z
      .string()
      .min(1, t('validation.requiredField'))
      .regex(/^[+]{1}(370)(\d{8})+$/, t('validation.number')),
    email: z
      .string()
      .min(1, t('validation.requiredField'))
      .email(t('validation.invalidEmail'))
      .refine((val) => /^[\u0000-\u007F]+$/.test(val), {
        message: t('validation.latinOnlyEmail'),
      }),
    topicId: z.coerce.number().min(1, t('validation.requiredField')),
    reason: z.string().optional(),
    personAgreement: z.literal(true, {
      errorMap: () => ({ message: t('validation.requiredField') }),
    }),
    fileIds: z.array(z.string()).optional(),
    files: z.any().optional(),
  });

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: userState.firstName || '',
      surname: userState.lastName || '',
      email: userState.email || '',
      phone: userState.phoneNumber || '',
      personalCode: userState.containsPersonalCode
        ? userState.personalCode
        : '',
      topicId: undefined,
      reason: '',
      personAgreement: false,
      files: [],
      fileIds: [],
    },
  });

  const sendForm = async (data) => {
    const response = await createRegistration('', {
      name: data.name,
      surname: data.surname,
      email: data.email,
      topic: selectedTopicLabel,
      phone: data.phone,
      personalCode: data.personalCode,
      reason: data.reason,
      files: data.files,
    });

    if (response) {
      setModalText(
        'Jūsų užklausa gauta. Budintis gydytojas su Jumis susisieks kaip įmanoma skubiau, bet ne vėliau nei per 1 darbo dieną',
      );
      setModalType('success');
    } else {
      setModalText('Įvyko klaida, prašome pakartoti vėliau');
      setModalType('error');
    }
    setModalVisible(true);
    checkboxRef.current.checked = false;
    form.reset();
  };

  useEffect(() => {
    setTopicList([
      { value: 1, label: 'Nedarbingumo išdavimas' },
      { value: 2, label: 'Vaisto ar MPP pratęsimas' },
      { value: 3, label: 'Ūmi liga ar lėtinės ligos paūmėjimas' },
      { value: 4, label: 'Kiti skubūs klausimai' },
    ]);
  }, []);

  const showForm =
    isTestEnv || userState.subscriptionInfo?.isSubscriptionInstitutionLocal;

  return (
    <>
      {showForm ? (
        <Card>
          <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-col gap-y-2">
              <Heading variant={'userAccountTitle'}>
                Susisiekti su budinčiu gydytoju
              </Heading>
            </div>
            <div className="hidden sm:block">
              <Button
                type="submit"
                variant={'light'}
                form="contact-duty-doctor-form"
              >
                <MaterialIcon icon={'edit'} size={16} />
                Siųsti prašymą
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <Form {...form}>
              <form
                id="contact-duty-doctor-form"
                onSubmit={form.handleSubmit(sendForm)}
                className="space-y-4"
              >
                <FormField
                  name="name"
                  control={form.control}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Vardas</FormLabel>
                      <FormControl>
                        <Input {...field} disabled />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  name="surname"
                  control={form.control}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Pavardė</FormLabel>
                      <FormControl>
                        <Input {...field} disabled />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  name="personalCode"
                  control={form.control}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Asmens kodas</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          disabled={userState.containsPersonalCode}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  name="phone"
                  control={form.control}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Telefono numeris</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  name="email"
                  control={form.control}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>El. pašto adresas</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <SelectField
                  innerRef={topicSelectRef}
                  disabled={!topicList.length}
                  error={form.formState.errors.topicId?.message}
                  value={form.watch('topicId')}
                  label={t('registration.form.dropDownTitle')}
                  name="topicId"
                  options={topicList}
                  greyTheme={true}
                  onChange={(value) => {
                    form.setValue('topicId', value);
                    const topic = topicList.find((t) => t.value === value);
                    if (topic) setSelectedTopicLabel(topic.label);
                  }}
                />
                <FormField
                  name="reason"
                  control={form.control}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Problema</FormLabel>
                      <FormControl>
                        <Textarea {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  name="personAgreement"
                  control={form.control}
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            ref={checkboxRef}
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                          <FormLabel className="m-0">
                            Sutinku, kad mano asmeniniai duomenys būtų panaudoti
                            registracijos tikslais
                          </FormLabel>
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FileUploadAsBase64
                  request={fileRequest}
                  error={form.formState.errors.fileIds?.message}
                  onChange={(value) => {
                    if (value) form.clearErrors('fileIds');
                    form.setValue('files', value);
                    form.setValue(
                      'fileIds',
                      value.map((f) => f.fileUid),
                    );
                  }}
                  value={form.watch('files')}
                  label={t('registration.form.fieldFileLabel')}
                  isGrey={true}
                />
                <div className="block sm:hidden">
                  <Button
                    type="submit"
                    variant={'light'}
                    form="contact-duty-doctor-form"
                  >
                    <MaterialIcon icon={'edit'} size={16} />
                    Siųsti prašymą
                  </Button>
                </div>
              </form>
            </Form>
          </CardContent>
        </Card>
      ) : (
        <p
          dangerouslySetInnerHTML={{ __html: t('registration.form.formTitle') }}
        />
      )}

      <Dialog open={modalVisible} onOpenChange={() => setModalVisible(false)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Dėkojame, Jūsų užklausa gauta</DialogTitle>
          </DialogHeader>
          {modalText && <DialogBody>{modalText}</DialogBody>}
          <DialogFooter>
            <Button
              variant={'secondary'}
              onClick={() => setModalVisible(false)}
            >
              Gerai
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default DutyDoctorRegistrationForm;
